import React, { useEffect, useRef, useState } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Avatar,
  alpha,
  IconButton,
} from '@mui/material';
import {
  Fullscreen,
  FullscreenExit,
} from '@mui/icons-material';
import VideoControls from './VideoControls';
import Whiteboard from './Whiteboard';

const AgoraVideoRoom = ({ sessionId, appId, channelName, token, uid, participants, currentUserId, whiteboardAppIdentifier, onLeave }) => {
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [isJoined, setIsJoined] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [maximizedParticipant, setMaximizedParticipant] = useState(null); // null, 'local', or user.uid

  // Helper function to get user info by user ID
  const getUserInfo = (userId) => {
    if (!participants || !userId) return null;
    if (participants.tutor?.id === userId) return participants.tutor;
    // Check students array (backend returns participants.students as an array)
    if (participants.students && Array.isArray(participants.students)) {
      const student = participants.students.find(s => s?.id === userId);
      if (student) return student;
    }
    return null;
  };

  // Helper function to convert UID back to user ID (reverse of the hash)
  // This is approximate - we'll match by checking if UID matches either participant's ID hash
  const getUserIdFromUid = (userUid) => {
    if (!participants || !userUid) return null;
    
    // Check if UID matches tutor's ID
    if (participants.tutor?.id) {
      const tutorIdStr = participants.tutor.id.toString();
      const tutorLast8 = tutorIdStr.slice(-8);
      const tutorUid = parseInt(tutorLast8, 16) % 2147483647;
      if (tutorUid === userUid) return participants.tutor.id;
    }
    
    // Check if UID matches any student's ID (students is an array)
    if (participants.students && Array.isArray(participants.students)) {
      for (const student of participants.students) {
        if (student?.id) {
          const studentIdStr = student.id.toString();
          const studentLast8 = studentIdStr.slice(-8);
          const studentUid = parseInt(studentLast8, 16) % 2147483647;
          if (studentUid === userUid) return student.id;
        }
      }
    }
    
    return null;
  };

  // Helper function to get user name for a UID
  const getParticipantName = (userUid) => {
    if (!participants) return `User ${userUid}`;
    
    // Try to match UID to a participant
    const matchedUserId = getUserIdFromUid(userUid);
    if (matchedUserId) {
      const userInfo = getUserInfo(matchedUserId);
      return userInfo?.fullName || `User ${userUid}`;
    }
    
    // Fallback: if current user is tutor, show first student name for remote users
    // If current user is student, show tutor name for remote users
    const currentUserInfo = getUserInfo(currentUserId);
    if (currentUserInfo) {
      const isCurrentUserTutor = participants.tutor?.id === currentUserId;
      const isCurrentUserStudent = participants.students?.some(s => s?.id === currentUserId);
      
      if (isCurrentUserTutor && participants.students?.length > 0) {
        // Current user is tutor, show first student's name as fallback
        return participants.students[0]?.fullName || `User ${userUid}`;
      } else if (isCurrentUserStudent) {
        // Current user is student, show tutor's name as fallback
        return participants.tutor?.fullName || `User ${userUid}`;
      }
    }
    return `User ${userUid}`;
  };

  // Helper function to get user initial
  const getUserInitial = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  // Get current user info
  const currentUserInfo = getUserInfo(currentUserId);
  const currentUserName = currentUserInfo?.fullName || 'You';
  const currentUserInitial = getUserInitial(currentUserName);

  const clientRef = useRef(null);
  const localVideoContainerRef = useRef(null);
  const remoteVideoContainerRef = useRef(null);
  const screenVideoTrackRef = useRef(null);
  const dataStreamRef = useRef(null);

  // Effect to play local video when track is available and enabled
  useEffect(() => {
    if (localVideoTrack && localVideoContainerRef.current && isVideoEnabled && !isScreenSharing) {
      const timer = setTimeout(() => {
        if (localVideoContainerRef.current && localVideoTrack && localVideoTrack.enabled) {
          try {
            // Check if video is already playing in this container
            const videoElement = localVideoContainerRef.current.querySelector('video');
            const isAlreadyPlaying = videoElement && !videoElement.paused && videoElement.readyState >= 2;
            
            // Only stop if video element exists AND is not already playing
            // If video element doesn't exist yet, Agora's play() will create it, so don't stop unnecessarily
            if (videoElement && !isAlreadyPlaying) {
              // Stop any existing playback (might be in a different container)
              try {
                localVideoTrack.stop();
              } catch (err) {
                // Ignore stop errors
              }
            }
            
            // Play the video track (Agora will create the element if it doesn't exist)
            // Only play if not already playing
            if (!isAlreadyPlaying) {
              const playResult = localVideoTrack.play(localVideoContainerRef.current);
              if (playResult && typeof playResult.then === 'function') {
                playResult.catch(err => {
                  console.error('Error playing local video:', err);
                });
              }
            }
          } catch (err) {
            console.error('Error playing local video:', err);
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    } else if (localVideoTrack && (!isVideoEnabled || isScreenSharing)) {
      // Stop video playback when disabled or screen sharing
      try {
        localVideoTrack.stop();
      } catch (err) {
        // Ignore errors when stopping
      }
    }
  }, [localVideoTrack, isVideoEnabled, isScreenSharing, maximizedParticipant]);

  // Effect to play remote videos when users or their tracks change
  useEffect(() => {
    const timers = [];
    
    remoteUsers.forEach((user) => {
      if (user.videoTrack) {
        // Small delay to ensure DOM element exists
        const timer = setTimeout(() => {
          const videoElement = document.getElementById(`remote-video-${user.uid}`);
          if (videoElement && user.videoTrack) {
            try {
              // Play the video track (will replace any existing playback)
              const playResult = user.videoTrack.play(videoElement);
              if (playResult && typeof playResult.then === 'function') {
                playResult.catch(err => {
                  console.error('Error playing remote video:', err);
                });
              }
            } catch (err) {
              console.error('Error playing remote video:', err);
            }
          }
        }, 200);
        
        timers.push(timer);
      }
    });
    
    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [remoteUsers]);

  // Effect to replay all videos when whiteboard is closed (video section becomes visible again)
  // Only run when whiteboard transitions from open to closed
  const prevShowWhiteboardRef = useRef(showWhiteboard);
  useEffect(() => {
    const wasWhiteboardOpen = prevShowWhiteboardRef.current;
    prevShowWhiteboardRef.current = showWhiteboard;
    
    // Only replay if whiteboard was just closed (transition from true to false)
    if (wasWhiteboardOpen && !showWhiteboard) {
      // Wait for DOM to be ready after whiteboard closes
      const timer = setTimeout(() => {
        // Replay local video if enabled
        if (localVideoTrack && localVideoContainerRef.current && isVideoEnabled && !isScreenSharing) {
          try {
            localVideoTrack.stop();
            const playResult = localVideoTrack.play(localVideoContainerRef.current);
            if (playResult && typeof playResult.then === 'function') {
              playResult.catch(err => {
                console.error('Error replaying local video after whiteboard close:', err);
              });
            }
          } catch (err) {
            console.error('Error replaying local video after whiteboard close:', err);
          }
        }

        // Replay all remote videos
        remoteUsers.forEach((user) => {
          if (user.videoTrack) {
            const videoElement = document.getElementById(`remote-video-${user.uid}`);
            if (videoElement && user.videoTrack) {
              try {
                // Stop and replay to ensure it's visible
                user.videoTrack.stop();
                const playResult = user.videoTrack.play(videoElement);
                if (playResult && typeof playResult.then === 'function') {
                  playResult.catch(err => {
                    console.error('Error replaying remote video after whiteboard close:', err);
                  });
                }
              } catch (err) {
                console.error('Error replaying remote video after whiteboard close:', err);
              }
            }
          }
        });
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [showWhiteboard, localVideoTrack, isVideoEnabled, isScreenSharing, remoteUsers]);

  // Effect to replay video when participant is maximized
  // Only run when maximizedParticipant transitions (not on every render)
  const prevMaximizedParticipantRef = useRef(maximizedParticipant);
  useEffect(() => {
    const wasMaximized = prevMaximizedParticipantRef.current;
    prevMaximizedParticipantRef.current = maximizedParticipant;
    
    if (maximizedParticipant && !showWhiteboard) {
      const timer = setTimeout(() => {
        if (maximizedParticipant === 'local') {
          // Replay local video in maximized view
          if (localVideoTrack && localVideoContainerRef.current && isVideoEnabled && !isScreenSharing) {
            try {
              localVideoTrack.stop();
              const playResult = localVideoTrack.play(localVideoContainerRef.current);
              if (playResult && typeof playResult.then === 'function') {
                playResult.catch(err => {
                  console.error('Error replaying local video in maximized view:', err);
                });
              }
            } catch (err) {
              console.error('Error replaying local video in maximized view:', err);
            }
          }
        } else {
          // Replay remote user video in maximized view - use unique ID for maximized container
          const maximizedUser = remoteUsers.find(u => u.uid === maximizedParticipant);
          if (maximizedUser && maximizedUser.videoTrack) {
            const videoElement = document.getElementById(`remote-video-maximized-${maximizedUser.uid}`);
            if (videoElement && maximizedUser.videoTrack) {
              try {
                // Stop video in the grid view first
                const gridVideoElement = document.getElementById(`remote-video-${maximizedUser.uid}`);
                if (gridVideoElement) {
                  try {
                    maximizedUser.videoTrack.stop();
                  } catch (err) {
                    // Ignore errors when stopping
                  }
                }
                // Play in maximized view
                const playResult = maximizedUser.videoTrack.play(videoElement);
                if (playResult && typeof playResult.then === 'function') {
                  playResult.catch(err => {
                    console.error('Error replaying remote video in maximized view:', err);
                  });
                }
              } catch (err) {
                console.error('Error replaying remote video in maximized view:', err);
              }
            }
          }
        }
      }, 300);

      return () => clearTimeout(timer);
    } else if (wasMaximized && !maximizedParticipant) {
      // Only replay when transitioning from maximized to minimized (not on every render)
      const timer = setTimeout(() => {
        // Replay local video in grid
        if (localVideoTrack && localVideoContainerRef.current && isVideoEnabled && !isScreenSharing) {
          try {
            localVideoTrack.stop();
            const playResult = localVideoTrack.play(localVideoContainerRef.current);
            if (playResult && typeof playResult.then === 'function') {
              playResult.catch(err => {
                console.error('Error replaying local video after minimize:', err);
              });
            }
          } catch (err) {
            console.error('Error replaying local video after minimize:', err);
          }
        }

        // Replay all remote videos in grid
        remoteUsers.forEach((user) => {
          if (user.videoTrack) {
            const videoElement = document.getElementById(`remote-video-${user.uid}`);
            if (videoElement && user.videoTrack) {
              try {
                user.videoTrack.stop();
                const playResult = user.videoTrack.play(videoElement);
                if (playResult && typeof playResult.then === 'function') {
                  playResult.catch(err => {
                    console.error('Error replaying remote video after minimize:', err);
                  });
                }
              } catch (err) {
                console.error('Error replaying remote video after minimize:', err);
              }
            }
          }
        });
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [maximizedParticipant, showWhiteboard, localVideoTrack, isVideoEnabled, isScreenSharing, remoteUsers]);

  useEffect(() => {
    let isMounted = true;
    let localTracks = [];

    const initAgora = async () => {
      try {
        if (!appId || !channelName || !token) {
          console.error('Missing Agora configuration:', { appId, channelName, token });
          setError('Missing Agora configuration');
          setIsLoading(false);
          return;
        }

        // Create Agora client
        const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
        clientRef.current = client;

        // Handle user joined - add them to remote users even without video
        client.on('user-joined', (user) => {
          setRemoteUsers((prev) => {
            if (prev.find((u) => u.uid === user.uid)) {
              return prev;
            }
            return [...prev, user];
          });
        });

        // Handle user published media
        client.on('user-published', async (user, mediaType) => {
          try {
            await client.subscribe(user, mediaType);
            
            // After subscription, tracks are immediately available on the user object
            // Update user in remoteUsers to include the track
            setRemoteUsers((prev) => {
              const existingUser = prev.find((u) => u.uid === user.uid);
              if (existingUser) {
                // Update existing user with new track
                const updatedUser = { ...existingUser };
                if (mediaType === 'video') {
                  // Get video track from user after subscription
                  updatedUser.videoTrack = user.videoTrack;
                }
                if (mediaType === 'audio') {
                  // Get audio track from user after subscription
                  updatedUser.audioTrack = user.audioTrack;
                }
                return prev.map((u) => (u.uid === user.uid ? updatedUser : u));
              } else {
                // Add new user if not already in list
                const newUser = { ...user };
                if (mediaType === 'video') {
                  newUser.videoTrack = user.videoTrack;
                }
                if (mediaType === 'audio') {
                  newUser.audioTrack = user.audioTrack;
                }
                return [...prev, newUser];
              }
            });
            
            // Play video/audio after state update - useEffect will also handle this
            if (mediaType === 'video' && user.videoTrack) {
              setTimeout(() => {
                const videoElement = document.getElementById(`remote-video-${user.uid}`);
                if (videoElement && user.videoTrack) {
                  try {
                    // Play the video track
                    const playResult = user.videoTrack.play(videoElement);
                    if (playResult && typeof playResult.then === 'function') {
                      playResult.catch(err => {
                        console.error('Error playing remote video:', err);
                      });
                    }
                  } catch (err) {
                    console.error('Error playing remote video:', err);
                  }
                }
              }, 500);
            }

            if (mediaType === 'audio' && user.audioTrack) {
              try {
                const playResult = user.audioTrack.play();
                if (playResult && typeof playResult.then === 'function') {
                  playResult.catch(err => {
                    console.error('Error playing remote audio:', err);
                  });
                }
              } catch (err) {
                console.error('Error playing remote audio:', err);
              }
            }
          } catch (err) {
            console.error('Error handling user-published:', err);
          }
        });

        // Handle user unpublished media
        client.on('user-unpublished', (user, mediaType) => {
          setRemoteUsers((prev) => {
            const existingUser = prev.find((u) => u.uid === user.uid);
            if (existingUser) {
              const updatedUser = { ...existingUser };
              if (mediaType === 'video') {
                updatedUser.videoTrack = null;
              }
              if (mediaType === 'audio') {
                updatedUser.audioTrack = null;
              }
              return prev.map((u) => (u.uid === user.uid ? updatedUser : u));
            }
            return prev;
          });
        });

        // Handle user left
        client.on('user-left', (user) => {
          setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid));
        });

        // Handle stream message (for whiteboard data)
        client.on('stream-message', (uid, data) => {
          // Forward to whiteboard component via custom event
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('agora-stream-message', {
              detail: { uid, data }
            }));
          }
        });

        // Join channel - use 0 for auto-assigned UID, or the provided uid
        const joinUid = uid !== undefined && uid !== null ? uid : 0;
        await client.join(appId, channelName, token, joinUid);
        
        if (!isMounted) {
          await client.leave();
          return;
        }

        setIsJoined(true);

        // Create data stream for whiteboard sharing
        try {
          const dataStream = await client.createDataStream({ ordered: true });
          dataStreamRef.current = dataStream;
        } catch (err) {
          console.error('Failed to create data stream:', err);
        }

        // Create local tracks
        const audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
        const videoTrack = await AgoraRTC.createCameraVideoTrack();

        localTracks = [audioTrack, videoTrack];

        if (!isMounted) {
          audioTrack.close();
          videoTrack.close();
          await client.leave();
          return;
        }

        setLocalAudioTrack(audioTrack);
        setLocalVideoTrack(videoTrack);

        // Publish tracks first (must be published before disabling)
        await client.publish([audioTrack, videoTrack]);

        // Disable tracks by default after publishing
        await audioTrack.setEnabled(false);
        await videoTrack.setEnabled(false);

        setIsLoading(false);
      } catch (err) {
        console.error('Error initializing Agora:', err);
        if (isMounted) {
          setError(err.message || 'Failed to join video call');
          setIsLoading(false);
        }
      }
    };

    if (appId && channelName && token) {
      initAgora();
    } else {
      setError('Missing required Agora configuration');
      setIsLoading(false);
    }

    // Cleanup
    return () => {
      isMounted = false;
      const cleanup = async () => {
        try {
          // Close screen track if exists
          if (screenVideoTrackRef.current) {
            try {
              screenVideoTrackRef.current.close();
            } catch (err) {
              // Ignore cleanup errors
            }
          }

          // Close local tracks
          localTracks.forEach(track => {
            if (track) {
              try {
                track.close();
              } catch (err) {
                // Ignore cleanup errors
              }
            }
          });

          // Leave channel
          if (clientRef.current) {
            try {
              await clientRef.current.leave();
            } catch (err) {
              // Ignore cleanup errors
            }
          }
        } catch (err) {
          // Ignore cleanup errors
        }
      };
      cleanup();
    };
  }, [appId, channelName, token, uid]);

  // Toggle video
  const toggleVideo = async () => {
    if (localVideoTrack) {
      const newEnabledState = !isVideoEnabled;
      await localVideoTrack.setEnabled(newEnabledState);
      setIsVideoEnabled(newEnabledState);
      
      // Let the useEffect handle playing the video - don't manually play here
      // This prevents double stop/play cycles that cause flickering
    }
  };

  // Toggle audio
  const toggleAudio = async () => {
    if (localAudioTrack) {
      if (isAudioEnabled) {
        await localAudioTrack.setEnabled(false);
      } else {
        await localAudioTrack.setEnabled(true);
      }
      setIsAudioEnabled(!isAudioEnabled);
    }
  };

  // Stop screen sharing helper function
  const stopScreenShare = async () => {
    if (screenVideoTrackRef.current) {
      try {
        // Unpublish screen track
        await clientRef.current.unpublish([screenVideoTrackRef.current]);
        screenVideoTrackRef.current.close();
        screenVideoTrackRef.current = null;
      } catch (err) {
        console.error('Error unpublishing screen track:', err);
      }
    }
    
    // Restore camera track if it exists
    if (localVideoTrack) {
      try {
        await clientRef.current.publish([localVideoTrack]);
        
        // Play camera track in local container
        if (localVideoContainerRef.current) {
          try {
            const playResult = localVideoTrack.play(localVideoContainerRef.current);
            if (playResult && typeof playResult.then === 'function') {
              playResult.catch(err => {
                console.error('Error playing camera track:', err);
              });
            }
          } catch (err) {
            console.error('Error playing camera track:', err);
          }
        }
      } catch (err) {
        console.error('Error publishing camera track:', err);
      }
    }
    
    // Always update state, even if there were errors
    setIsScreenSharing(false);
  };

  // Screen sharing
  const toggleScreenShare = async () => {
    try {
      if (!isScreenSharing) {
        // Start screen sharing
        // createScreenVideoTrack can return a single track or an array [videoTrack, audioTrack]
        const screenTrackResult = await AgoraRTC.createScreenVideoTrack({}, 'auto');
        const screenTrack = Array.isArray(screenTrackResult) ? screenTrackResult[0] : screenTrackResult;
        
        // Handle when screen sharing is stopped by browser (e.g., user clicks stop in browser dialog)
        // Use the underlying MediaStreamTrack's 'ended' event if 'on' method is not available
        if (screenTrack && typeof screenTrack.on === 'function') {
          screenTrack.on('track-ended', async () => {
            await stopScreenShare();
          });
        } else if (screenTrack) {
          // Fallback: Listen to the underlying MediaStreamTrack's 'ended' event
          const mediaStreamTrack = screenTrack.getMediaStreamTrack();
          if (mediaStreamTrack) {
            mediaStreamTrack.addEventListener('ended', async () => {
              await stopScreenShare();
            });
          }
        }
        
        // Unpublish camera track
        if (localVideoTrack) {
          await clientRef.current.unpublish([localVideoTrack]);
        }
        
        // Publish screen track
        await clientRef.current.publish([screenTrack]);
        
        // Play screen track in local container
        if (localVideoContainerRef.current) {
          try {
            const playResult = screenTrack.play(localVideoContainerRef.current);
            if (playResult && typeof playResult.then === 'function') {
              playResult.catch(err => {
                console.error('Error playing screen track:', err);
              });
            }
          } catch (err) {
            console.error('Error playing screen track:', err);
          }
        }
        
        screenVideoTrackRef.current = screenTrack;
        setIsScreenSharing(true);
      } else {
        // Stop screen sharing
        await stopScreenShare();
      }
    } catch (err) {
      console.error('Error toggling screen share:', err);
      // If error occurs during start, ensure state is correct
      if (isScreenSharing) {
        await stopScreenShare();
      }
    }
  };

  // Leave call
  const handleLeave = async () => {
    if (clientRef.current) {
      if (screenVideoTrackRef.current) {
        screenVideoTrackRef.current.close();
      }
      if (localVideoTrack) {
        localVideoTrack.close();
      }
      if (localAudioTrack) {
        localAudioTrack.close();
      }
      await clientRef.current.leave();
    }
    if (onLeave) {
      onLeave();
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6">Joining video call...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#2a2a2a',
        overflow: 'hidden',
      }}
    >
      {/* Main content area */}
      <Box sx={{ flex: 1, display: 'flex', gap: showWhiteboard ? 0 : 2, p: showWhiteboard ? 0 : 2, minHeight: 0, minWidth: 0, position: 'relative' }}>
        {/* Video section - full size when whiteboard is not shown, floating overlay when whiteboard is shown */}
        <Box
          sx={{
            ...(showWhiteboard ? {
              position: 'absolute',
              top: 16,
              left: 16,
              zIndex: 10,
              width: 'auto',
              maxWidth: '300px',
            } : maximizedParticipant ? {
              position: 'absolute',
              top: 16,
              left: 16,
              zIndex: 10,
              width: 'auto',
              maxWidth: '300px',
            } : {
              flex: 1,
            }),
            display: 'flex',
            flexDirection: 'column',
            gap: (showWhiteboard || maximizedParticipant) ? 0.5 : 2,
            minHeight: 0,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {/* All users video grid (including local user) */}
          <Box
            ref={remoteVideoContainerRef}
            sx={{
              ...((showWhiteboard || maximizedParticipant) ? {
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                maxHeight: '400px',
                marginTop: '10px',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '2px',
                },
              } : {
                flex: 1,
                display: 'grid',
                gridTemplateColumns: (remoteUsers.length + 1) <= 1 ? '1fr' : (remoteUsers.length + 1) <= 2 ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)',
                gridTemplateRows: (remoteUsers.length + 1) <= 2 ? '1fr' : 'repeat(2, 1fr)',
                gap: 2,
                minHeight: 0,
                minWidth: 0,
              }),
            }}
          >
            {/* Local user video card */}
            <Paper
              sx={{
                position: 'relative',
                bgcolor: '#1a1a1a',
                borderRadius: (showWhiteboard || maximizedParticipant) ? 1 : 2,
                overflow: 'hidden',
                width: (showWhiteboard || maximizedParticipant) ? '120px' : '100%',
                height: (showWhiteboard || maximizedParticipant) ? '90px' : '100%',
                minHeight: (showWhiteboard || maximizedParticipant) ? '90px' : 0,
                minWidth: (showWhiteboard || maximizedParticipant) ? '120px' : 0,
                flexShrink: 0,
                display: maximizedParticipant === 'local' ? 'none' : 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: (showWhiteboard || maximizedParticipant) ? '1px solid' : '2px solid',
                borderColor: 'primary.main',
                boxShadow: (showWhiteboard || maximizedParticipant) ? '0 2px 4px rgba(0, 0, 0, 0.5)' : '0 2px 8px rgba(0, 0, 0, 0.3)',
              }}
            >
              <Box
                ref={(el) => {
                  localVideoContainerRef.current = el;
                }}
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: 0,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& video': {
                    width: '100%',
                    height: '100%',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }
                }}
              />
              {(!isVideoEnabled || !localVideoTrack || isScreenSharing || (localVideoTrack && !localVideoTrack.enabled)) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#2d2d2d',
                    color: 'white',
                    gap: 1,
                  }}
                >
                  <Avatar
                    sx={{
                      width: showWhiteboard ? 40 : 80,
                      height: showWhiteboard ? 40 : 80,
                      bgcolor: 'primary.main',
                      fontSize: showWhiteboard ? '1rem' : '2rem',
                    }}
                  >
                    {currentUserInitial}
                  </Avatar>
                  {!showWhiteboard && (
                    <Typography variant="h6" sx={{ color: 'white' }}>
                      {currentUserName}
                    </Typography>
                  )}
                </Box>
              )}
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: (showWhiteboard || maximizedParticipant) ? 2 : 8,
                  left: (showWhiteboard || maximizedParticipant) ? 2 : 8,
                  bgcolor: alpha('#000', 0.85),
                  color: '#ffffff',
                  px: (showWhiteboard || maximizedParticipant) ? 0.5 : 1.5,
                  py: (showWhiteboard || maximizedParticipant) ? 0.25 : 0.75,
                  borderRadius: 1,
                  fontWeight: 600,
                  fontSize: (showWhiteboard || maximizedParticipant) ? '0.6rem' : '0.75rem',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  maxWidth: (showWhiteboard || maximizedParticipant) ? '100px' : 'auto',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {(showWhiteboard || maximizedParticipant) ? 'You' : `${currentUserName} (You)`}
              </Typography>
              {!showWhiteboard && !maximizedParticipant && (
                <IconButton
                  onClick={() => {
                    if (showWhiteboard) setShowWhiteboard(false);
                    setMaximizedParticipant('local');
                  }}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: alpha('#000', 0.6),
                    color: 'white',
                    '&:hover': {
                      bgcolor: alpha('#000', 0.8),
                    },
                    zIndex: 2,
                  }}
                  size="small"
                >
                  <Fullscreen fontSize="small" />
                </IconButton>
              )}
            </Paper>

            {/* Remote users */}
            {remoteUsers.map((user) => {
              const participantName = getParticipantName(user.uid);
              const participantInitial = getUserInitial(participantName);
              // Check if video track exists and is enabled
              // Check if video track exists and is enabled
              const hasVideo = user.videoTrack && user.videoTrack.enabled !== false;
              
              return (
                <Paper
                  key={user.uid}
                  sx={{
                    position: 'relative',
                    bgcolor: '#1a1a1a',
                    borderRadius: (showWhiteboard || maximizedParticipant) ? 1 : 2,
                    overflow: 'hidden',
                    width: (showWhiteboard || maximizedParticipant) ? '120px' : '100%',
                    height: (showWhiteboard || maximizedParticipant) ? '90px' : '100%',
                    minHeight: (showWhiteboard || maximizedParticipant) ? '90px' : 0,
                    minWidth: (showWhiteboard || maximizedParticipant) ? '120px' : 0,
                    flexShrink: 0,
                    display: maximizedParticipant === user.uid ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: (showWhiteboard || maximizedParticipant) ? '0 2px 4px rgba(0, 0, 0, 0.5)' : '0 2px 8px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  <Box
                    id={`remote-video-${user.uid}`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& video': {
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }
                    }}
                  />
                  {!hasVideo && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#2d2d2d',
                      }}
                    >
                      <Avatar
                        sx={{
                          width: showWhiteboard ? 40 : 80,
                          height: showWhiteboard ? 40 : 80,
                          bgcolor: 'primary.main',
                          fontSize: showWhiteboard ? '1rem' : '2rem',
                        }}
                      >
                        {participantInitial}
                      </Avatar>
                    </Box>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      bottom: (showWhiteboard || maximizedParticipant) ? 2 : 8,
                      left: (showWhiteboard || maximizedParticipant) ? 2 : 8,
                      bgcolor: alpha('#000', 0.85),
                      color: '#ffffff',
                      px: (showWhiteboard || maximizedParticipant) ? 0.5 : 1.5,
                      py: (showWhiteboard || maximizedParticipant) ? 0.25 : 0.75,
                      borderRadius: 1,
                      fontWeight: 600,
                      fontSize: (showWhiteboard || maximizedParticipant) ? '0.6rem' : '0.75rem',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      maxWidth: (showWhiteboard || maximizedParticipant) ? '100px' : 'auto',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {participantName}
                  </Typography>
                  {!showWhiteboard && !maximizedParticipant && (
                    <IconButton
                      onClick={() => {
                        if (showWhiteboard) setShowWhiteboard(false);
                        setMaximizedParticipant(user.uid);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: alpha('#000', 0.6),
                        color: 'white',
                        '&:hover': {
                          bgcolor: alpha('#000', 0.8),
                        },
                        zIndex: 2,
                      }}
                      size="small"
                    >
                      <Fullscreen fontSize="small" />
                    </IconButton>
                  )}
                </Paper>
              );
            })}
            {remoteUsers.length === 0 && !showWhiteboard && !maximizedParticipant && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255, 255, 255, 0.7)',
                  bgcolor: alpha('#1a1a1a', 0.5),
                  borderRadius: 2,
                  p: 3,
                  border: '1px dashed rgba(255, 255, 255, 0.2)',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Waiting for other participant...
                </Typography>
              </Box>
            )}
          </Box>

          {/* Local video */}
          <Paper
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: '400px',
              maxHeight: '225px',
              bgcolor: '#1a1a1a',
              borderRadius: 2,
              overflow: 'hidden',
              alignSelf: 'flex-end',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            }}
          >
            <Box
              ref={localVideoContainerRef}
              sx={{
                width: '100%',
                height: '100%',
                bgcolor: '#1a1a1a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& video': {
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }
              }}
            />
            {(!isVideoEnabled || !localVideoTrack) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#1a1a1a',
                }}
              >
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: 'primary.main',
                    fontSize: '1.5rem',
                  }}
                >
                  {currentUserInitial}
                </Avatar>
              </Box>
            )}
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                bgcolor: alpha('#000', 0.85),
                color: '#ffffff',
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                fontWeight: 600,
                fontSize: '0.75rem',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              {currentUserName}
            </Typography>
          </Paper>
        </Box>

        {/* Whiteboard section - takes full width and height when shown */}
        {showWhiteboard && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, width: '100%', height: '100%', pt: 2 }}>
            <Whiteboard 
              sessionId={sessionId}
              appIdentifier={whiteboardAppIdentifier || process.env.REACT_APP_AGORA_WHITEBOARD_APP_IDENTIFIER}
              onClose={() => setShowWhiteboard(false)}
            />
          </Box>
        )}

        {/* Maximized participant video - takes full screen */}
        {maximizedParticipant && !showWhiteboard && (
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: '#1a1a1a',
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {maximizedParticipant === 'local' ? (
              <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box
                  ref={localVideoContainerRef}
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& video': {
                      width: '100%',
                      height: '100%',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }
                  }}
                />
                {(!isVideoEnabled || !localVideoTrack || isScreenSharing || (localVideoTrack && !localVideoTrack.enabled)) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#2d2d2d',
                      color: 'white',
                      gap: 2,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 120,
                        height: 120,
                        bgcolor: 'primary.main',
                        fontSize: '3rem',
                      }}
                    >
                      {currentUserInitial}
                    </Avatar>
                    <Typography variant="h4" sx={{ color: 'white' }}>
                      {currentUserName}
                    </Typography>
                  </Box>
                )}
                <Typography
                  variant="h6"
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: 16,
                    bgcolor: alpha('#000', 0.85),
                    color: '#ffffff',
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    fontWeight: 600,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  {currentUserName} (You)
                </Typography>
                <IconButton
                  onClick={() => setMaximizedParticipant(null)}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    bgcolor: alpha('#000', 0.6),
                    color: 'white',
                    '&:hover': {
                      bgcolor: alpha('#000', 0.8),
                    },
                  }}
                  size="large"
                >
                  <FullscreenExit />
                </IconButton>
              </Box>
            ) : (
              (() => {
                const maximizedUser = remoteUsers.find(u => u.uid === maximizedParticipant);
                if (!maximizedUser) return null;
                const participantName = getParticipantName(maximizedUser.uid);
                const participantInitial = getUserInitial(participantName);
                const hasVideo = maximizedUser.videoTrack && maximizedUser.videoTrack.enabled !== false;
                
                return (
                  <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box
                      id={`remote-video-maximized-${maximizedUser.uid}`}
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '& video': {
                          width: '100%',
                          height: '100%',
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }
                      }}
                    />
                    {!hasVideo && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#2d2d2d',
                          gap: 2,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 120,
                            height: 120,
                            bgcolor: 'primary.main',
                            fontSize: '3rem',
                          }}
                        >
                          {participantInitial}
                        </Avatar>
                        <Typography variant="h4" sx={{ color: 'white' }}>
                          {participantName}
                        </Typography>
                      </Box>
                    )}
                    <Typography
                      variant="h6"
                      sx={{
                        position: 'absolute',
                        bottom: 16,
                        left: 16,
                        bgcolor: alpha('#000', 0.85),
                        color: '#ffffff',
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        fontWeight: 600,
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      {participantName}
                    </Typography>
                    <IconButton
                      onClick={() => setMaximizedParticipant(null)}
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        bgcolor: alpha('#000', 0.6),
                        color: 'white',
                        '&:hover': {
                          bgcolor: alpha('#000', 0.8),
                        },
                      }}
                      size="large"
                    >
                      <FullscreenExit />
                    </IconButton>
                  </Box>
                );
              })()
            )}
          </Box>
        )}
      </Box>

      {/* Controls */}
      <VideoControls
        isVideoEnabled={isVideoEnabled}
        isAudioEnabled={isAudioEnabled}
        isScreenSharing={isScreenSharing}
        showWhiteboard={showWhiteboard}
        onToggleVideo={toggleVideo}
        onToggleAudio={toggleAudio}
        onToggleScreenShare={toggleScreenShare}
        onToggleWhiteboard={() => setShowWhiteboard(!showWhiteboard)}
        onLeave={handleLeave}
      />
    </Box>
  );
};

export default AgoraVideoRoom;

